/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Grid, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import { useNavigate } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDDatePicker from 'components/MDDatePicker';
import { api } from '../../../../api';
import { DATE_RANGE_PRESETS, resolveDateRange } from '../../../../utils/dateRangePresets';

function GenderStatCard({ label, count, color, icon, loading, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s, transform 0.15s',
        '&:hover': onClick ? { boxShadow: 6, transform: 'translateY(-2px)' } : {},
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={1} px={2}>
        <MDBox
          variant="gradient"
          bgColor={color}
          color="white"
          coloredShadow={color}
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          mt={-3}
        >
          {React.cloneElement(icon, { sx: { color: '#fff', fontSize: 32 } })}
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.25}>
          <MDTypography variant="button" fontWeight="light" color="text">{label}</MDTypography>
          <MDTypography variant="h4">{loading ? <CircularProgress size={22} /> : count}</MDTypography>
        </MDBox>
      </MDBox>
      <MDBox pb={2} px={2} pt={1}>
        <MDTypography variant="caption" color="text">
          {onClick ? 'Click to view patient list →' : ''}
        </MDTypography>
      </MDBox>
    </Card>
  );
}

export default function RegistrationStats() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState('current_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [countryId, setCountryId] = useState('');
  const [countries, setCountries] = useState([]);
  const [stats, setStats] = useState({ male: 0, female: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.master.getActiveCountries()
      .then((res) => setCountries(res?.data?.countries || []))
      .catch(() => setCountries([]));
  }, []);

  const range = resolveDateRange(preset, { from: customFrom, to: customTo });

  const fetchStats = useCallback(async () => {
    if (!range.from || !range.to) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.contacts.getRegistrationStats({
        country_id: countryId || undefined,
        date_from: range.from,
        date_to: range.to,
      });
      setStats(res?.data || { male: 0, female: 0, total: 0 });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load registration stats');
      setStats({ male: 0, female: 0, total: 0 });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, countryId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const goToPatientList = (gender) => {
    const params = new URLSearchParams({
      gender, created_from: range.from, created_to: range.to, page: '1', limit: '10',
    });
    if (countryId) params.set('country_id', countryId);
    navigate(`/patient-list?${params.toString()}`);
  };

  return (
    <Card sx={{ p: 2.5 }}>
      <MDBox display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} mb={2.5}>
        <MDTypography variant="h6" fontWeight="bold">New Registrations</MDTypography>
        <MDBox display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Date Range</InputLabel>
            <Select label="Date Range" value={preset} onChange={(e) => setPreset(e.target.value)}>
              {DATE_RANGE_PRESETS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {preset === 'custom' && (
            <>
              <MDDatePicker label="From" value={customFrom} onChange={(v) => setCustomFrom(v || '')} maxDate={customTo || undefined} />
              <MDDatePicker label="To" value={customTo} onChange={(v) => setCustomTo(v || '')} minDate={customFrom || undefined} />
            </>
          )}
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Country</InputLabel>
            <Select label="Country" value={countryId} onChange={(e) => setCountryId(e.target.value)}>
              <MenuItem value="">All Countries</MenuItem>
              {countries.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.country_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
      </MDBox>

      {error && (
        <MDTypography variant="caption" color="error" sx={{ display: 'block', mb: 1.5 }}>
          {error}
        </MDTypography>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <GenderStatCard
            label="Male Registrations"
            count={stats.male}
            color="info"
            icon={<MaleIcon fontSize="medium" />}
            loading={loading}
            onClick={() => goToPatientList('M')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <GenderStatCard
            label="Female Registrations"
            count={stats.female}
            color="primary"
            icon={<FemaleIcon fontSize="medium" />}
            loading={loading}
            onClick={() => goToPatientList('F')}
          />
        </Grid>
      </Grid>
    </Card>
  );
}
